import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Table, PageSizeType } from 'design-system-web';
import { useChannelAdminContext } from 'hooks';
import { ProductCategory } from 'graphql-query-contracts';

import { useCategoryListQuery } from '../helpers/useCategoryListQuery';
import { useGetCategoriesTableColumns } from '../helpers/useGetCategoriesTableColums';
import { deleteCategoryMutation } from '../helpers/deleteCategoryMutation';

export function CategoriesTable() {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const [pageSize, setPageSize] = useState<PageSizeType>(50);
  const [page, setPage] = useState(0);

  const { data: queryResponse, refetch } = useCategoryListQuery({
    page,
    pageSize,
  });

  const onDelete = async (category: ProductCategory) => {
    if (!channel?._id) return;

    try {
      await window.Alert.confirm({
        title: t('abp.productsServices.deleteCategory.title', {
          category: category.name,
        }),
        message: t('abp.productsServices.deleteCategory.confirmMessage'),
        confirmText: t('abp.productsServices.deleteCategory.delete'),
        cancelText: t('abp.productsServices.deleteCategory.cancel'),
        confirmButtonStyle: {
          backgroundColor: '#C7200A',
          borderColor: '#C7200A',
          color: 'white',
        },
      });
    } catch {
      // User cancelled
      return;
    }

    try {
      await deleteCategoryMutation(category.productCategoryId, channel._id);

      window.Toast.show(
        t('abp.productsServices.deleteCategory.success', {
          categoryName: category.name,
        })
      );

      await refetch();
    } catch (error) {
      let errorMessage = t('abp.productsServices.deleteCategory.error');

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      try {
        await window.Alert.confirm({
          title: t('abp.productsServices.deleteCategory.errorTitle', {
            category: category.name,
          }),
          message: errorMessage,
          confirmText: t('abp.productsServices.deleteCategory.gotIt'),
          cancelText: '', // Hide cancel button
          confirmButtonStyle: {
            width: '20%',
          },
        });
      } catch {
        // User dismissed the alert
      }
    }
  };

  const categoriesTableColumns = useGetCategoriesTableColumns(onDelete);

  return (
    <Table
      columns={categoriesTableColumns}
      data={queryResponse?.listProductCategories.productCategory || []}
      disableSorting
      isLoading={false}
      pagination="server"
      totalRows={queryResponse?.listProductCategories.pageInfo?.total}
      pageSize={pageSize}
      page={page}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
    />
  );
}
